import * as React from 'react';

import {emptyObject} from '@packages/utilities/fp/utils';
import {emotionStyled, tokens, useSize} from '@packages/utilities/styled';
import {useFloatingTree, useListItem, useMergeRefs} from '@floating-ui/react';

import {CheckDefaultIcon, ChevronSmallRightIcon} from '../../../icons';
import {getDefinedValue, renderElement} from '../../../utils';
import {mergeProps} from '../../../utils/mergeProps';

import {HStack} from '../../Stack';
import {Slot} from '../../Slot';
import {Text} from '../../Text';

import {type MenuItemProps} from '../_types';
import {useMenuContext} from '../useMenu';
import {useMenuContentContext} from '../useMenuContent';
import {useMenuGroupContext} from '../MenuGroup';

const MENU_ITEM_ROLE = {
  none: 'menuitem',
  single: 'menuitemradio',
  multiple: 'menuitemcheckbox',
};

const MenuItemCheck = ({isChecked = false}) => {
  const {shouldShowSelectedCheck} = useMenuContext() || emptyObject;
  const {selectionMode, shouldHideSelectedCheck} =
    useMenuGroupContext() || emptyObject;

  if (
    (!shouldShowSelectedCheck || shouldHideSelectedCheck) &&
    selectionMode === 'none'
  )
    return null;
  return (
    <IconWrapper hasSetWidth>
      {isChecked ? <CheckDefaultIcon colorVariant="primary" /> : null}
    </IconWrapper>
  );
};

const PrefixTextWrapper = emotionStyled(HStack)({
  whiteSpace: 'nowrap',
  minWidth: 0,
  '[data-sc="Icon"]': {
    color: '$textSecondary',
  },
});

const IconWrapper = emotionStyled('div')(
  {
    alignContent: 'center',
    minHeight: tokens.ui.menu.item.prefix.height,
  },
  {
    variants: {
      hasSetWidth: {
        true: {
          width: '$4',
        },
      },
    },
  }
);

const MenuItemView = emotionStyled('div')(
  {
    background: '$uiMenuContentBackground',
    border: 0,
    borderRadius: 0,
    color: '$textPrimary',
    cursor: 'default',
    display: 'block',
    justifyContent: 'start',
    outline: 'none',
    padding: tokens.ui.menu.item.padding,
    textDecoration: 'none',
    width: '100%',

    '&:hover, &:focus-within, &[aria-expanded="true"]': {
      background: '$uiBackgroundHover',
      [`${PrefixTextWrapper} [data-sc="Icon"]`]: {
        color: '$textPrimary',
      },
    },
  },
  {
    componentName: 'MenuItem',
    variants: {
      isDisabled: {
        true: {
          cursor: 'not-allowed',
          opacity: 0.5,

          '&:focus-within': {
            background: '$uiBackgroundHover',
          },

          // Must be placed after focus-within to override on Mouse interaction
          '&:hover': {
            background: '$uiMenuContentBackground',
            [`${PrefixTextWrapper} [data-sc="Icon"]`]: {
              color: '$textSecondary',
            },
          },
        },
      },
      isNested: {
        true: {
          padding: tokens.ui.menu.item.nestedPadding,
        },
      },
    },
  }
);

const SuffixView = emotionStyled(HStack)({
  width: 'auto',
  '[data-sc="Icon"]': {
    color: '$textSecondary',
  },
});

const TextView = emotionStyled(Text)({
  textWrap: 'nowrap',
});

type MenuItemWrapperProps = MenuItemProps & {
  isNested?: boolean;
};

export const MenuItemWrapper = React.forwardRef<
  HTMLDivElement,
  MenuItemWrapperProps
>(
  (
    {
      as = 'button',
      asChild,
      children,
      href,
      isChecked,
      isDisabled = false,
      isNested = false,
      prefix,
      spacing: spacingProp,
      suffix,
      ...props
    },
    forwardedRef
  ) => {
    const {shouldTruncateText, verticalItemAlignment = 'start'} =
      useMenuContext() || emptyObject; // This is for unit tests where individual MenuItems are tested in isolation.
    const size = useSize();
    const spacing = spacingProp ?? (size === 'x-large' ? 2 : 1);

    return (
      <MenuItemView
        as={as}
        className="MenuItem"
        /**
         * @TODO
         * This is because href is not a valid prop on a div.
         * However, because as can render an anchor tag, `MenuItemView` _can_ accept an href.
         *
         * We need to convert this to an 'asChild' pattern to allow for the `href` prop to be passed to the anchor tag.
         * @see https://webflow.atlassian.net/browse/DS-868
         */
        // @ts-expect-error - TS-2322 - href is not a prop on div.
        href={href}
        isChecked={isChecked}
        isDisabled={isDisabled}
        isNested={isNested}
        ref={forwardedRef}
        {...props}
      >
        <HStack
          align={verticalItemAlignment}
          isFullWidth
          justify="start"
          spacing={1}
        >
          <PrefixTextWrapper
            align={verticalItemAlignment}
            justify="start"
            spacing={spacing}
          >
            {isNested ? null : <MenuItemCheck isChecked={isChecked} />}
            {prefix ? (
              <IconWrapper>
                {renderElement(prefix, {
                  color: '$textPrimary',
                })}
              </IconWrapper>
            ) : null}
            {asChild ? (
              <Slot>{children}</Slot>
            ) : (
              <TextView
                textAlign="start"
                truncateToLines={shouldTruncateText ? 1 : undefined}
              >
                {children}
              </TextView>
            )}
          </PrefixTextWrapper>
          <SuffixView align={verticalItemAlignment} justify="end" spacing={0}>
            {suffix ? (
              <IconWrapper>
                {renderElement(suffix, {color: '$textSecondary'})}
              </IconWrapper>
            ) : null}
            {isNested ? (
              <IconWrapper>
                <ChevronSmallRightIcon colorVariant="primary" />
              </IconWrapper>
            ) : null}
          </SuffixView>
        </HStack>
      </MenuItemView>
    );
  }
);

export const MenuItem = React.forwardRef<HTMLDivElement, MenuItemProps>(
  (
    {
      as: asProp,
      asChild,
      children,
      disabled,
      href,
      isChecked: isCheckedProp,
      isDisabled: isDisabledProp = false,
      note,
      onClick,
      prefix,
      shouldCloseOnClickItem: shouldCloseOnClickItemProp,
      spacing,
      suffix,
      value,
      ...props
    }: MenuItemProps,
    forwardedRef
  ) => {
    const {getIsSelected, selectionMode, toggleSelectedValue} =
      useMenuGroupContext();
    const isChecked =
      isCheckedProp ??
      (selectionMode !== 'none' && value ? getIsSelected(value) : undefined);

    const role = MENU_ITEM_ROLE[selectionMode];

    const {activeIndex, getItemProps, menuItemNote, setMenuItemNote} =
      useMenuContentContext();
    const item = useListItem();
    const tree = useFloatingTree();
    const isActive = item.index === activeIndex;

    React.useEffect(() => {
      if (isActive && note && note !== menuItemNote) {
        setMenuItemNote(note);
      } else if (isActive && !note && menuItemNote) {
        setMenuItemNote(null);
      } else if (activeIndex === null && menuItemNote) {
        setMenuItemNote(null);
      }
    }, [activeIndex, isActive, menuItemNote, note, setMenuItemNote]);

    /**
     * We're intentionally intervening the disabled prop from being
     * passed into the HTML.
     *
     * This is because the `disabled` HTML attribute makes it so the button
     * cannot be focused. We want the MenuItem to be focusable even when it
     * is meant to be disabled so that users can still access its content.
     * (ex. When a disabled MenuItem is wrapped in a Tooltip, users cannot
     * access the Tooltip unless the MenuItem is focusable)
     *
     * Instead, we'll use the aria-disabled attribute to semantically disable the MenuItem.
     */
    const isDisabled = isDisabledProp || disabled;

    // Drop href when disabled so middle-click / "Open in new tab" cannot
    // bypass isDisabled (left-click is already blocked in handleItemClick).
    const resolvedHref = isDisabled ? undefined : href || undefined;
    const isLink = Boolean(resolvedHref);
    const as = getDefinedValue(asProp, isLink ? 'a' : undefined);

    const handleItemClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      /**
       * If shouldCloseOnClickItem has been provided as a prop, use that value.
       * Otherwise, if the MenuItem is disabled, shouldCloseOnClickItem will be set to false.
       * If neither shouldCloseOnClickItem or a disabled prop has been provided,
       * the value will remain undefined.
       */
      let shouldCloseOnClickItem = shouldCloseOnClickItemProp;

      if (isDisabled) {
        event.preventDefault();
        event.stopPropagation();
        shouldCloseOnClickItem = false;
      } else {
        onClick?.(event);
        if (value) {
          toggleSelectedValue(value);
        }
      }

      tree?.events.emit('click', {
        shouldCloseOnClickIndividualItem: shouldCloseOnClickItem,
        originalEvent: event,
      });
    };

    const floatingProps = getItemProps();
    const mergedProps = mergeProps(floatingProps, {
      ...props,
      onClick: handleItemClick,
    });

    return (
      <MenuItemWrapper
        aria-describedby={isActive ? 'spring-menu-note' : undefined}
        as={as}
        asChild={asChild}
        href={resolvedHref}
        isChecked={isChecked}
        isDisabled={isDisabled}
        onClick={handleItemClick}
        prefix={prefix}
        ref={useMergeRefs([item.ref, forwardedRef])}
        role={role}
        spacing={spacing}
        suffix={suffix}
        tabIndex={isActive ? 0 : -1}
        {...mergedProps}
      >
        {children}
      </MenuItemWrapper>
    );
  }
);
